import {
  IApplicationFormData,
  IWizardReviewSummary,
} from '../../models/application-form'

export type RenterHomeTab = 'applications' | 'household' | 'profile'
export type AccessStep = 'mobile' | 'otp'

export type ScaffoldSubmissionStatus =
  | 'submitted'
  | 'viewed'
  | 'revoked'
  | 'expired'
  | 'not_sent'
  | 'awaiting_email'

export type ScaffoldSubmission = {
  id: string
  canSelectForFoundPlace?: boolean
  title: string
  address: string
  submittedAt: string
  agentName: string
  agentPhone: string
  agentEmail?: string
  agents?: ScaffoldSubmissionAgent[]
  status: ScaffoldSubmissionStatus
  photoClassName: string
  viewedBy?: string
  viewedAt?: string
  expiresAt?: string
  revokedAt?: string
  expiresInDays: number
  placeId?: string
  applicationId?: number
  applicationFullName?: string
  submittedByName?: string
  includedMemberCount?: number
  isHouseholdCompositionOutdated?: boolean
  householdCompositionChangeType?: 'none' | 'added' | 'removed' | 'changed'
  addedHouseholdMemberNames?: string[]
  removedHouseholdMemberNames?: string[]
  currentHouseholdMemberCount?: number
  submittedHouseholdMemberCount?: number
}

export type ScaffoldSubmissionAgent = {
  name: string
  phone: string
  email?: string
  status: ScaffoldSubmissionStatus
  deliveryChannel?: string
  deliveryWarning?: string
  sentAt?: string
  viewedAt?: string
  isPrimary: boolean
}

export type SubmitToPropertyPrefill = {
  propertyAddress?: string
  propertyPlaceId?: string
  agentName?: string
  agentPhone?: string
  agentEmail?: string
  agents?: Array<{
    agentName: string
    agentPhone: string
    agentEmail?: string
  }>
  resubmittedFromSubmissionId?: string
}

export const AU_MOBILE_REGEX = /^04\d{8}$/
export const AU_FIXED_LINE_REGEX = /^0[2378]\d{8}$/
export const AU_AGENT_CONTACT_PHONE_REGEX = /^(?:04\d{8}|0[2378]\d{8})$/
export const RENTER_HOME_PATH = '/my-doora-card'

export const sampleSubmissions: ScaffoldSubmission[] = [
  {
    id: 'doora-sample-1',
    title: '23 Howard St, Randwick NSW 2031',
    address: '23 Howard St, Randwick NSW 2031',
    submittedAt: '2025-12-18T00:00:00.000Z',
    agentName: 'Sarah Johnson',
    agentPhone: '0290000001',
    agentEmail: 'sarah.johnson@example.com',
    status: 'viewed',
    photoClassName: 'renter-property-photo-one',
    viewedBy: 'Sarah',
    viewedAt: '2026-01-08T00:00:00.000Z',
    expiresInDays: 7,
  },
  {
    id: 'doora-sample-2',
    title: '15 Moira Cres, Randwick NSW 2031',
    address: '15 Moira Crescent, Randwick NSW 2031',
    submittedAt: '2025-10-15T00:00:00.000Z',
    agentName: 'Michael Chen',
    agentPhone: '0290000002',
    agentEmail: 'michael.chen@example.com',
    status: 'submitted',
    photoClassName: 'renter-property-photo-two',
    expiresInDays: 28,
  },
]

export const normalizeMobile = (mobileNumber: string): string => mobileNumber.replace(/\D/g, '')

export const normalizeAustralianPhone = (phoneNumber?: string): string => {
  const digits = normalizeMobile(phoneNumber ?? '')
  if (digits.length === 11 && digits.startsWith('61')) {
    return `0${digits.slice(2)}`
  }

  return digits
}

export const isValidAustralianMobile = (mobileNumber?: string): boolean =>
  AU_MOBILE_REGEX.test(normalizeMobile(mobileNumber ?? ''))

export const isValidAustralianAgentPhone = (phoneNumber?: string): boolean =>
  AU_AGENT_CONTACT_PHONE_REGEX.test(normalizeAustralianPhone(phoneNumber))

export const normalizeAustralianAbn = (abn?: string): string =>
  (abn ?? '').replace(/\D/g, '').slice(0, 11)

export const formatAustralianAbnInput = (abn?: string): string => {
  const digits = normalizeAustralianAbn(abn)

  if (digits.length <= 2) {
    return digits
  }

  if (digits.length <= 5) {
    return `${digits.slice(0, 2)} ${digits.slice(2)}`
  }

  if (digits.length <= 8) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5)}`
  }

  return `${digits.slice(0, 2)} ${digits.slice(2, 5)} ${digits.slice(5, 8)} ${digits.slice(8)}`
}

export const formatAustralianMobileInput = (mobileNumber?: string): string => {
  const digits = normalizeMobile(mobileNumber ?? '').slice(0, 10)

  if (digits.length <= 4) {
    return digits
  }

  if (digits.length <= 7) {
    return `${digits.slice(0, 4)} ${digits.slice(4)}`
  }

  return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
}

export const formatAustralianMobile = (mobileNumber?: string): string => {
  const digits = normalizeMobile(mobileNumber ?? '')
  if (!AU_MOBILE_REGEX.test(digits)) {
    return mobileNumber ?? ''
  }

  return formatAustralianMobileInput(digits)
}

export const formatAustralianAgentPhone = (phoneNumber?: string): string => {
  const digits = normalizeAustralianPhone(phoneNumber)

  if (AU_MOBILE_REGEX.test(digits)) {
    return formatAustralianMobileInput(digits)
  }

  if (AU_FIXED_LINE_REGEX.test(digits)) {
    return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`
  }

  return phoneNumber ?? ''
}

export const formatDate = (value?: string): string => {
  if (!value) {
    return 'Not available'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return 'Not available'
  }

  return new Intl.DateTimeFormat('en-AU', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(parsed)
}

export const formatDayMonth = (value?: string): string => {
  if (!value) {
    return 'Not available'
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return 'Not available'
  }

  return new Intl.DateTimeFormat('en-AU', {
    day: 'numeric',
    month: 'short',
  }).format(parsed)
}

export const toStatusLabel = (status?: string): string => {
  if (!status) {
    return 'Draft'
  }

  return status
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .split(' ')
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ')
}

export const getSessionIdFromSearch = (search: string): string | undefined => {
  const value = new URLSearchParams(search).get('sessionId')?.trim()
  return value || undefined
}

export const getApplicantTypeSummary = (
  summary: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): string => {
  const applicationType = draft?.applicationType ?? summary?.applicationType ?? 'Individual'
  const isSoloApplication = applicationType === 'Individual' || applicationType === 'CompanyTrust'
  const summaryStatuses = summary?.coApplicantStatuses ?? []
  const activeSummaryCoApplicantCount = summaryStatuses.filter(
    (applicant) =>
      !isDeclinedInviteStatus(applicant.status, applicant.declinedAtUtc, applicant.declineReason) &&
      applicant.status?.trim().toLowerCase() !== 'revoked'
  ).length
  const activeDraftCoApplicantCount = (draft?.coApplicants ?? []).filter(
    (applicant) => 
        !isDeclinedInviteStatus(applicant.status, applicant.declinedAtUtc, applicant.declineReason) &&
        applicant.status?.trim().toLowerCase() !== 'revoked'
  ).length
  const coApplicantCount = isSoloApplication
    ? 0
    : draft
    ? activeDraftCoApplicantCount
    : summaryStatuses.length > 0
    ? activeSummaryCoApplicantCount
    : Math.max(0, summary?.coApplicantCount ?? 0)
  const applicantCount = coApplicantCount + 1
  const occupantCount = draft?.otherOccupants.length ?? summary?.otherOccupantCount ?? 0
  const applicantLabel = `${applicantCount} applicant${applicantCount === 1 ? '' : 's'}`
  const occupantLabel =
    occupantCount > 0 ? ` + ${occupantCount} occupant${occupantCount === 1 ? '' : 's'}` : ''

  return `${applicationType} · ${applicantLabel}${occupantLabel}`
}

export const getApplicantName = (
  summary: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): string => {
  const summaryName = summary?.applicantName?.trim()
  if (summaryName) {
    return summaryName
  }

  const draftName = `${draft?.firstName ?? ''} ${draft?.lastName ?? ''}`.trim()
  return draftName || 'Renter'
}

export const getApplicantFirstName = (
  summary: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): string => {
  const draftFirstName = draft?.firstName?.trim()
  if (draftFirstName) {
    return draftFirstName
  }

  return getApplicantName(summary, draft).split(' ').filter(Boolean)[0] || 'Renter'
}

export const getPrimaryRoleLabel = (draft: IApplicationFormData | null): string => {
  const role = draft?.renterRole ?? 'primary'
  return role === 'co-applicant' ? 'Co-applicant' : 'Primary applicant'
}

export const getPrimaryRoleLabelWithHoushold = (
  household: IWizardReviewSummary | null,
  draft: IApplicationFormData | null
): string => {
  if (household?.renterRole) {
    const summaryRole = household.renterRole ?? 'primary'
    return summaryRole === 'co-applicant' ? 'Co-applicant' : 'Primary applicant'
  }

  const role = draft?.renterRole ?? 'primary'
  return role === 'co-applicant' ? 'Co-applicant' : 'Primary applicant'
}

export const hasCompletedDooraCard = (
  summary: IWizardReviewSummary | null
): boolean => summary?.cardReadiness?.isComplete === true

export const getInitials = (name: string): string =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('') || 'A'

export const formatInvitedAgo = (value?: string): string => {
  if (!value) return 'Invited'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return 'Invited'
  }

  const days = Math.max(1, Math.round((Date.now() - parsed.getTime()) / 86400000))
  return `Invited ${days} ${days === 1 ? 'day' : 'days'} ago`
}

export const isInviteExpired = (value?: string): boolean => {
  if (!value) return true

  const normalized = value.trim()
  const hasTimezone = /(?:z|[+-]\d{2}:?\d{2})$/i.test(normalized)
  const parsed = Date.parse(hasTimezone ? normalized : `${normalized}Z`)

  if (Number.isNaN(parsed)) {
    return true
  }

  return Date.now() > parsed
}

export const isSharedApplication = (applicationType?: string) => {
  if (
    applicationType === 'Housemates' ||
    applicationType === 'Family' ||
    applicationType === 'Couple'
  )
    return true

  return false
}

export const isDeclinedInviteStatus = (
    status?: string,
    declinedAtUtc?: string | Date,
    declineReason?: string
): boolean =>
    Boolean(declinedAtUtc) ||
    declineReason?.trim().toLocaleLowerCase() === 'this_isnt_me' ||
    status?.trim().toLowerCase() === 'declined';
